<template>
  <div class="jv-app" :data-theme="theme">
    <header class="jv-topbar">
      <div class="jv-brand">
        <span class="jv-logo">{ }</span>
        <div class="jv-brand-txt">
          <h1>JSON Viewer</h1>
          <span class="jv-brand-sub">Visualiza, busca y explora JSON</span>
        </div>
      </div>

      <div class="jv-search-wrap">
        <svg class="jv-search-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref="search"
          type="search"
          v-model="searchQuery"
          placeholder="Buscar clave o valor... (Ctrl+F)"
        />
        <span v-if="searchCountText" class="jv-search-count">{{ searchCountText }}</span>
      </div>

      <div class="jv-actions">
        <button @click="toggleTheme" class="jv-theme-btn" :title="theme === 'dark' ? 'Modo claro' : 'Modo oscuro'">
          <svg v-if="theme === 'dark'" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2" />
            <path d="M12 20v2" />
            <path d="m4.93 4.93 1.41 1.41" />
            <path d="m17.66 17.66 1.41 1.41" />
            <path d="M2 12h2" />
            <path d="M20 12h2" />
            <path d="m4.93 19.07 1.41-1.41" />
            <path d="m17.66 6.34 1.41-1.41" />
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        </button>
      </div>
    </header>

    <main class="jv-layout" ref="layout" :style="layoutStyle">
      <section class="jv-pane jv-pane-left">
        <div class="jv-pane-header">
          <span>Editor</span>
          <span :class="['jv-status', 'jv-status-' + statusKind]">{{ statusText }}</span>
        </div>
        <textarea
          ref="editor"
          v-model="editorValue"
          spellcheck="false"
          placeholder="Pega tu JSON aquí..."
        ></textarea>
        <div v-if="errorMsg" class="jv-error">{{ errorMsg }}</div>
      </section>

      <div class="jv-splitter" @mousedown="startDrag"></div>

      <section class="jv-pane jv-pane-right">
        <div class="jv-pane-header">
          <span>Vista de árbol</span>
          <div class="jv-btn-group jv-btn-group--compact">
            <button @click="setAllExpanded(true)" title="Expandir todo">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="7 10 12 5 17 10" />
                <polyline points="7 14 12 19 17 14" />
              </svg>
              Expandir
            </button>
            <button @click="setAllExpanded(false)" title="Colapsar todo">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="17 11 12 16 7 11" />
                <polyline points="17 5 12 10 7 5" />
              </svg>
              Colapsar
            </button>
          </div>
        </div>
        <div ref="tree" class="jv-tree" tabindex="0" aria-label="Vista de árbol JSON"></div>
        <div v-if="selectedPath" class="jv-path-bar">
          <div class="jv-path-bar__row">
            <span class="jv-path-label">Ruta</span>
            <code class="jv-path">{{ selectedPath }}</code>
            <button @click="copyPath" class="jv-icon-btn">Copiar</button>
          </div>
          <div class="jv-path-bar__row">
            <span class="jv-path-label">Valor</span>
            <code class="jv-path jv-path--value">{{ selectedValueDisplay }}</code>
            <button @click="copyValue" class="jv-icon-btn jv-icon-btn--primary">Copiar</button>
          </div>
        </div>
      </section>
    </main>

    <div v-if="toastVisible" class="jv-toast">{{ toastMsg }}</div>
  </div>
</template>

<script>
export default {
  name: 'JsonViewer',
  data() {
    return {
      editorValue: '',
      searchQuery: '',
      statusKind: 'empty',
      statusText: 'Vacío',
      errorMsg: '',
      selectedPath: '',
      selectedValue: undefined,
      hasSelection: false,
      searchCountText: '',
      theme: localStorage.getItem('jv-theme') || 'light',
      layoutStyle: {},
      toastVisible: false,
      toastMsg: '',
      currentData: undefined,
      selectedRow: null,
      dragging: false,
      parseTimer: null,
      searchTimer: null,
      toastTimer: null,
    }
  },
  computed: {
    selectedValueDisplay() {
      const v = this.selectedValue
      if (v === undefined) return ''
      if (typeof v === 'string') return v
      try {
        return JSON.stringify(v)
      } catch (e) {
        return String(v)
      }
    },
  },
  watch: {
    editorValue() {
      clearTimeout(this.parseTimer)
      this.parseTimer = setTimeout(this.parseAndRender, 200)
    },
    searchQuery() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.runSearch, 150)
    },
    theme(val) {
      localStorage.setItem('jv-theme', val)
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('mouseup', this.onMouseUp)
    this.parseAndRender()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('mouseup', this.onMouseUp)
    clearTimeout(this.parseTimer)
    clearTimeout(this.searchTimer)
    clearTimeout(this.toastTimer)
  },
  methods: {
    setStatus(kind, text) {
      this.statusKind = kind
      this.statusText = text
    },
    parseAndRender() {
      const tree = this.$refs.tree
      const text = (this.editorValue || '').trim()
      if (!text) {
        this.currentData = undefined
        if (tree) tree.innerHTML = '<div class="jv-tree-empty">Pega o escribe JSON válido en el editor para verlo aquí.</div>'
        this.errorMsg = ''
        this.setStatus('empty', 'Vacío')
        this.selectedPath = ''
        return
      }
      try {
        this.currentData = JSON.parse(text)
        this.errorMsg = ''
        const nodes = this.countNodes(this.currentData)
        this.setStatus('ok', 'Válido · ' + nodes + ' nodos')
        this.renderTree(this.currentData)
        if (this.searchQuery) this.runSearch()
      } catch (err) {
        this.errorMsg = err.message
        this.setStatus('error', 'Error de sintaxis')
      }
    },
    countNodes(val) {
      if (val === null || typeof val !== 'object') return 1
      let n = 1
      if (Array.isArray(val)) {
        for (const v of val) n += this.countNodes(v)
      } else {
        for (const k in val) n += this.countNodes(val[k])
      }
      return n
    },
    typeOf(v) {
      if (v === null) return 'null'
      if (Array.isArray(v)) return 'array'
      return typeof v
    },
    renderTree(data) {
      const tree = this.$refs.tree
      tree.innerHTML = ''
      this.selectedRow = null
      this.selectedPath = ''
      this.selectedValue = undefined
      const root = this.buildNode('$', data, true, [])
      tree.appendChild(root)
    },
    buildNode(label, value, isRoot, pathParts) {
      const type = this.typeOf(value)
      const node = document.createElement('div')
      node.className = 'jv-node'

      const row = document.createElement('span')
      row.className = 'jv-node-row'
      row.dataset.path = this.pathToString(pathParts)

      const caret = document.createElement('span')
      caret.className = 'jv-caret'

      const isContainer = type === 'object' || type === 'array'
      const size = isContainer ? (type === 'array' ? value.length : Object.keys(value).length) : 0
      const hasChildren = isContainer && size > 0

      caret.textContent = hasChildren ? '▶' : ''
      if (!hasChildren) caret.classList.add('jv-leaf')
      row.appendChild(caret)

      if (!isRoot) {
        const labelSpan = document.createElement('span')
        if (typeof label === 'number') {
          labelSpan.className = 'jv-index'
          labelSpan.textContent = label
        } else {
          labelSpan.className = 'jv-key'
          labelSpan.textContent = JSON.stringify(label).slice(1, -1)
        }
        row.appendChild(labelSpan)
      }

      if (isContainer) {
        const open = document.createElement('span')
        open.className = 'jv-bracket'
        open.textContent = type === 'array' ? '[' : '{'
        row.appendChild(open)

        if (!hasChildren) {
          const close = document.createElement('span')
          close.className = 'jv-bracket'
          close.textContent = type === 'array' ? ']' : '}'
          row.appendChild(close)
        } else {
          const count = document.createElement('span')
          count.className = 'jv-count'
          count.textContent = size + ' ' + (size === 1 ? 'elemento' : 'elementos')
          row.appendChild(count)
        }
      } else {
        const valSpan = document.createElement('span')
        valSpan.className = 'jv-value jv-value-' + type
        if (type === 'string') valSpan.textContent = JSON.stringify(value)
        else if (type === 'null') valSpan.textContent = 'null'
        else valSpan.textContent = String(value)
        row.appendChild(valSpan)
      }

      node.appendChild(row)

      row.addEventListener('click', (e) => {
        e.stopPropagation()
        if (hasChildren) this.toggleNode(node)
        this.selectRow(row, pathParts, value)
      })

      node._value = value
      node._pathParts = pathParts

      if (isContainer && hasChildren) {
        const childrenWrap = document.createElement('div')
        childrenWrap.className = 'jv-children jv-collapsed'
        childrenWrap.dataset.rendered = 'false'
        childrenWrap.dataset.type = type

        if (size <= 50) {
          this.renderChildren(childrenWrap, value, pathParts, type)
          childrenWrap.dataset.rendered = 'true'
        }
        node.appendChild(childrenWrap)

        if (isRoot) {
          this.ensureChildrenRendered(node)
          childrenWrap.classList.remove('jv-collapsed')
          caret.classList.add('jv-expanded')
        }

        const closeRow = document.createElement('span')
        closeRow.className = 'jv-bracket'
        closeRow.style.paddingLeft = '18px'
        closeRow.textContent = type === 'array' ? ']' : '}'
        closeRow.style.display = 'none'
        closeRow.dataset.role = 'close'
        node.appendChild(closeRow)
      }

      return node
    },
    renderChildren(wrap, value, pathParts, type) {
      const frag = document.createDocumentFragment()
      if (type === 'array') {
        for (let i = 0; i < value.length; i++) {
          frag.appendChild(this.buildNode(i, value[i], false, pathParts.concat([i])))
        }
      } else {
        for (const k of Object.keys(value)) {
          frag.appendChild(this.buildNode(k, value[k], false, pathParts.concat([k])))
        }
      }
      wrap.appendChild(frag)
    },
    ensureChildrenRendered(node) {
      const wrap = node.querySelector(':scope > .jv-children')
      if (wrap && wrap.dataset.rendered === 'false') {
        const type = wrap.dataset.type
        this.renderChildren(wrap, node._value, node._pathParts, type)
        wrap.dataset.rendered = 'true'
      }
    },
    toggleNode(node) {
      const wrap = node.querySelector(':scope > .jv-children')
      const caret = node.querySelector(':scope > .jv-node-row > .jv-caret')
      const close = node.querySelector(':scope > [data-role="close"]')
      if (!wrap) return
      this.ensureChildrenRendered(node)
      const collapsed = wrap.classList.toggle('jv-collapsed')
      caret.classList.toggle('jv-expanded', !collapsed)
      if (close) close.style.display = collapsed ? 'inline' : 'none'
    },
    setAllExpanded(expanded) {
      const tree = this.$refs.tree
      if (!tree) return
      tree.querySelectorAll('.jv-node').forEach(n => {
        const wrap = n.querySelector(':scope > .jv-children')
        if (!wrap) return
        this.ensureChildrenRendered(n)
        wrap.classList.toggle('jv-collapsed', !expanded)
        const caret = n.querySelector(':scope > .jv-node-row > .jv-caret')
        if (caret) caret.classList.toggle('jv-expanded', expanded)
        const close = n.querySelector(':scope > [data-role="close"]')
        if (close) close.style.display = expanded ? 'none' : 'inline'
      })
    },
    pathToString(parts) {
      let s = '$'
      for (const p of parts) {
        if (typeof p === 'number') s += '[' + p + ']'
        else if (/^[a-zA-Z_$][\w$]*$/.test(p)) s += '.' + p
        else s += '[' + JSON.stringify(p) + ']'
      }
      return s
    },
    selectRow(row, pathParts, value) {
      if (this.selectedRow) this.selectedRow.classList.remove('jv-selected')
      this.selectedRow = row
      row.classList.add('jv-selected')
      this.selectedPath = this.pathToString(pathParts)
      this.selectedValue = value
    },
    async copyPath() {
      try {
        await navigator.clipboard.writeText(this.selectedPath)
        this.showToast('Ruta copiada')
      } catch (e) {
        this.showToast('No se pudo copiar')
      }
    },
    async copyValue() {
      const v = this.selectedValue
      let text
      if (typeof v === 'string') text = v
      else if (v === undefined) text = ''
      else text = JSON.stringify(v, null, 2)
      try {
        await navigator.clipboard.writeText(text)
        this.showToast('Valor copiado')
      } catch (e) {
        this.showToast('No se pudo copiar')
      }
    },
    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark'
    },
    runSearch() {
      const tree = this.$refs.tree
      if (!tree) return
      const q = (this.searchQuery || '').trim().toLowerCase()
      tree.querySelectorAll('.jv-node-row.jv-match').forEach(r => r.classList.remove('jv-match'))
      if (!q) {
        this.searchCountText = ''
        return
      }
      this.setAllExpanded(true)
      let count = 0
      tree.querySelectorAll('.jv-node-row').forEach(row => {
        const text = row.textContent.toLowerCase()
        if (text.includes(q)) {
          row.classList.add('jv-match')
          count++
        }
      })
      this.searchCountText = count > 0 ? count + ' coincidencia' + (count === 1 ? '' : 's') : 'Sin resultados'
      const first = tree.querySelector('.jv-node-row.jv-match')
      if (first) first.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    showToast(msg) {
      this.toastMsg = msg
      this.toastVisible = true
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false
      }, 2200)
    },
    onKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && !e.shiftKey) {
        if (document.activeElement !== this.$refs.editor) {
          e.preventDefault()
          this.$refs.search.focus()
          this.$refs.search.select()
        }
        return
      }

      if (!this.selectedRow) return
      const ae = document.activeElement
      if (ae === this.$refs.editor || ae === this.$refs.search) return

      const key = e.key
      if (key === 'Enter') {
        e.preventDefault()
        this.copyValue()
        return
      }
      if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'ArrowLeft' && key !== 'ArrowRight') return
      e.preventDefault()

      const tree = this.$refs.tree
      if (!tree) return

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        const rows = Array.from(tree.querySelectorAll('.jv-node-row')).filter(r => !r.closest('.jv-collapsed'))
        const idx = rows.indexOf(this.selectedRow)
        if (idx === -1) return
        const nextIdx = key === 'ArrowDown' ? Math.min(rows.length - 1, idx + 1) : Math.max(0, idx - 1)
        this.activateRow(rows[nextIdx])
      } else if (key === 'ArrowRight') {
        const node = this.selectedRow.parentElement
        const wrap = node && node.querySelector(':scope > .jv-children')
        if (wrap) {
          if (wrap.classList.contains('jv-collapsed')) {
            this.toggleNode(node)
          } else {
            const firstChild = wrap.querySelector(':scope > .jv-node > .jv-node-row')
            if (firstChild) this.activateRow(firstChild)
          }
        }
      } else if (key === 'ArrowLeft') {
        const node = this.selectedRow.parentElement
        const wrap = node && node.querySelector(':scope > .jv-children')
        if (wrap && !wrap.classList.contains('jv-collapsed')) {
          this.toggleNode(node)
        } else if (node) {
          const parentNode = node.parentElement && node.parentElement.closest('.jv-node')
          if (parentNode) {
            const parentRow = parentNode.querySelector(':scope > .jv-node-row')
            if (parentRow) this.activateRow(parentRow)
          }
        }
      }
    },
    activateRow(row) {
      if (!row) return
      const node = row.parentElement
      const pathParts = (node && node._pathParts) || []
      const value = node ? node._value : undefined
      this.selectRow(row, pathParts, value)
      row.scrollIntoView({ block: 'nearest' })
    },
    startDrag(e) {
      this.dragging = true
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      e.preventDefault()
    },
    onMouseMove(e) {
      if (!this.dragging) return
      const layout = this.$refs.layout
      if (!layout) return
      const rect = layout.getBoundingClientRect()
      const isVertical = window.innerWidth <= 780
      if (isVertical) {
        const y = e.clientY - rect.top
        const pct = Math.max(15, Math.min(85, (y / rect.height) * 100))
        this.layoutStyle = { gridTemplateRows: pct + '% 6px ' + (100 - pct) + '%' }
      } else {
        const x = e.clientX - rect.left
        const pct = Math.max(15, Math.min(85, (x / rect.width) * 100))
        this.layoutStyle = { gridTemplateColumns: pct + '% 6px ' + (100 - pct) + '%' }
      }
    },
    onMouseUp() {
      if (this.dragging) {
        this.dragging = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    },
  },
}
</script>

<style lang="scss">
.jv-app {
  --jv-bg: #ffffff;
  --jv-bg-soft: #f6f7f9;
  --jv-bg-elev: #ffffff;
  --jv-border: #e4e7ec;
  --jv-text: #1a1d23;
  --jv-text-muted: #6b7280;
  --jv-accent: #3b82f6;
  --jv-accent-soft: #dbeafe;
  --jv-success: #10b981;
  --jv-danger: #ef4444;
  --jv-warn: #f59e0b;

  --jv-c-key: #7c3aed;
  --jv-c-string: #059669;
  --jv-c-number: #2563eb;
  --jv-c-boolean: #d97706;
  --jv-c-null: #6b7280;
  --jv-c-bracket: #374151;

  --jv-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.04);
  --jv-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  --jv-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--jv-bg);
  color: var(--jv-text);
  font-family: var(--jv-sans);
  font-size: 14px;
  overflow: hidden;
  z-index: 1;

  &[data-theme='dark'] {
    --jv-bg: #0f1115;
    --jv-bg-soft: #151821;
    --jv-bg-elev: #1b1f2a;
    --jv-border: #2a2f3a;
    --jv-text: #e6e8ee;
    --jv-text-muted: #9aa3b2;
    --jv-accent: #60a5fa;
    --jv-accent-soft: #1e3a8a;
    --jv-success: #34d399;
    --jv-danger: #f87171;
    --jv-warn: #fbbf24;

    --jv-c-key: #c4b5fd;
    --jv-c-string: #6ee7b7;
    --jv-c-number: #93c5fd;
    --jv-c-boolean: #fcd34d;
    --jv-c-null: #9aa3b2;
    --jv-c-bracket: #cbd5e1;

    --jv-shadow: 0 1px 2px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  * { box-sizing: border-box; }

  .jv-topbar {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 20px;
    padding: 12px 20px;
    background: linear-gradient(180deg, var(--jv-bg-elev) 0%, var(--jv-bg-soft) 100%);
    border-bottom: 1px solid var(--jv-border);

    @media (max-width: 780px) {
      grid-template-columns: 1fr auto;
      gap: 10px 12px;
    }
  }

  .jv-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    h1 {
      font-size: 15px;
      margin: 0;
      font-weight: 600;
      letter-spacing: -0.01em;
      color: var(--jv-text);
      line-height: 1.1;
    }
  }

  .jv-brand-txt {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .jv-brand-sub {
    font-size: 11px;
    color: var(--jv-text-muted);
    font-weight: 500;
    letter-spacing: 0.01em;
  }

  .jv-logo {
    font-family: var(--jv-mono);
    font-weight: 700;
    font-size: 17px;
    background: linear-gradient(135deg, var(--jv-accent) 0%, #6366f1 100%);
    color: #fff;
    padding: 6px 11px;
    border-radius: 8px;
    box-shadow: 0 2px 8px -2px rgba(59, 130, 246, 0.45);
    line-height: 1;
    flex-shrink: 0;
  }

  .jv-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-self: end;

    @media (max-width: 780px) {
      grid-column: 1 / -1;
      justify-self: stretch;
      justify-content: flex-end;
    }
  }

  .jv-btn-group {
    display: inline-flex;
    background: var(--jv-bg);
    border: 1px solid var(--jv-border);
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

    button {
      background: transparent;
      border: 0;
      color: var(--jv-text-muted);
      padding: 5px 10px;
      font-size: 12.5px;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border-radius: 5px;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
      text-transform: none;
      letter-spacing: normal;

      &:hover {
        background: var(--jv-bg-soft);
        color: var(--jv-text);
      }

      &:active {
        transform: translateY(1px);
      }

      svg {
        opacity: 0.7;
      }
    }

    &--compact {
      padding: 2px;
      border-radius: 6px;

      button {
        padding: 3px 8px;
        font-size: 11px;
        gap: 4px;
        border-radius: 4px;
      }
    }
  }

  .jv-theme-btn {
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid var(--jv-border);
    border-radius: 999px;
    background: var(--jv-bg);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

    color: var(--jv-text-muted);

    &:hover {
      background: var(--jv-accent-soft);
      border-color: var(--jv-accent);
      color: var(--jv-accent);
      transform: translateY(-1px);
    }

    svg {
      display: block;
    }
  }

  button {
    appearance: none;
    background: var(--jv-bg-soft);
    color: var(--jv-text);
    border: 1px solid var(--jv-border);
    padding: 6px 12px;
    font-size: 13px;
    font-family: inherit;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    font-weight: 500;

    &:hover {
      background: var(--jv-accent-soft);
      border-color: var(--jv-accent);
      color: var(--jv-accent);
    }

    &:active {
      transform: translateY(1px);
    }
  }

  .jv-icon-btn {
    padding: 6px 10px;
    min-width: 34px;
  }

  .jv-layout {
    flex: 1;
    display: grid;
    grid-template-columns: 15% 6px calc(85% - 6px);
    overflow: hidden;
    background: var(--jv-bg);
  }

  .jv-pane {
    display: flex;
    flex-direction: column;
    background: var(--jv-bg);
    overflow: hidden;
    min-width: 200px;
  }

  .jv-pane-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: var(--jv-bg-soft);
    border-bottom: 1px solid var(--jv-border);
    font-size: 12px;
    color: var(--jv-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    gap: 10px;
    min-height: 36px;
  }

  .jv-pane-right .jv-pane-header {
    justify-content: flex-start;
  }

  .jv-status {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 999px;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }
  .jv-status-empty {
    background: var(--jv-bg-elev);
    color: var(--jv-text-muted);
    border: 1px solid var(--jv-border);
  }
  .jv-status-ok {
    background: rgba(16, 185, 129, 0.12);
    color: var(--jv-success);
  }
  .jv-status-error {
    background: rgba(239, 68, 68, 0.12);
    color: var(--jv-danger);
  }

  textarea {
    flex: 1;
    border: none;
    resize: none;
    padding: 14px;
    font-family: var(--jv-mono);
    font-size: 13px;
    line-height: 1.6;
    background: var(--jv-bg);
    color: var(--jv-text);
    outline: none;
    tab-size: 2;

    &::placeholder {
      color: var(--jv-text-muted);
    }
  }

  .jv-error {
    padding: 10px 14px;
    background: rgba(239, 68, 68, 0.1);
    color: var(--jv-danger);
    border-top: 1px solid rgba(239, 68, 68, 0.3);
    font-family: var(--jv-mono);
    font-size: 12px;
    white-space: pre-wrap;
    max-height: 120px;
    overflow: auto;
  }

  .jv-splitter {
    background: var(--jv-border);
    cursor: col-resize;
    transition: background 0.15s;

    &:hover {
      background: var(--jv-accent);
    }
  }

  .jv-search-wrap {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--jv-bg);
    border: 1px solid var(--jv-border);
    border-radius: 999px;
    padding: 6px 14px 6px 12px;
    max-width: 420px;
    width: 100%;
    justify-self: center;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
      border-color: var(--jv-accent);
      box-shadow: 0 0 0 3px var(--jv-accent-soft);
    }

    @media (max-width: 780px) {
      max-width: none;
      grid-column: 1 / -1;
    }
  }

  .jv-search-icon {
    color: var(--jv-text-muted);
    flex-shrink: 0;
  }

  input[type='search'] {
    flex: 1;
    min-width: 0;
    padding: 0;
    font-size: 13px;
    border: 0;
    background: transparent;
    color: var(--jv-text);
    font-family: inherit;
    outline: none;

    &::placeholder {
      color: var(--jv-text-muted);
    }

    &::-webkit-search-cancel-button {
      -webkit-appearance: none;
      height: 14px;
      width: 14px;
      border-radius: 999px;
      background: var(--jv-border);
      cursor: pointer;
      background-image: linear-gradient(45deg, transparent 46%, var(--jv-text-muted) 46%, var(--jv-text-muted) 54%, transparent 54%),
        linear-gradient(-45deg, transparent 46%, var(--jv-text-muted) 46%, var(--jv-text-muted) 54%, transparent 54%);
    }
  }

  .jv-search-count {
    font-size: 11px;
    color: var(--jv-text-muted);
    white-space: nowrap;
    padding: 2px 8px;
    background: var(--jv-bg-soft);
    border-radius: 999px;
    font-weight: 500;
  }

  .jv-tree {
    flex: 1;
    overflow: auto;
    padding: 14px;
    font-family: var(--jv-mono);
    font-size: 13px;
    line-height: 1.6;
    outline: none;
  }

  .jv-tree-empty {
    color: var(--jv-text-muted);
    font-style: italic;
    padding: 20px;
    text-align: center;
  }

  .jv-node {
    display: block;
    position: relative;
  }

  .jv-node-row {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    padding: 1px 4px;
    border-radius: 4px;
    cursor: pointer;
    max-width: 100%;

    &:hover {
      background: var(--jv-bg-soft);
    }

    &.jv-selected {
      background: var(--jv-accent-soft);
    }
  }

  .jv-caret {
    display: inline-block;
    width: 14px;
    color: var(--jv-text-muted);
    user-select: none;
    font-size: 10px;
    transition: transform 0.15s;
    text-align: center;

    &.jv-expanded {
      transform: rotate(90deg);
    }
    &.jv-leaf {
      visibility: hidden;
    }
  }

  .jv-key {
    color: var(--jv-c-key);
    font-weight: 500;

    &::after {
      content: ': ';
      color: var(--jv-c-bracket);
    }
  }

  .jv-index {
    color: var(--jv-text-muted);
    font-size: 11px;

    &::after {
      content: ': ';
      color: var(--jv-c-bracket);
    }
  }

  .jv-value {
    white-space: pre-wrap;
    word-break: break-word;
  }
  .jv-value-string { color: var(--jv-c-string); }
  .jv-value-number { color: var(--jv-c-number); }
  .jv-value-boolean { color: var(--jv-c-boolean); }
  .jv-value-null { color: var(--jv-c-null); font-style: italic; }

  .jv-bracket {
    color: var(--jv-c-bracket);
  }

  .jv-count {
    color: var(--jv-text-muted);
    font-size: 11px;
    margin-left: 4px;
    font-style: italic;
  }

  .jv-children {
    padding-left: 20px;
    border-left: 1px solid var(--jv-border);
    margin-left: 6px;

    &.jv-collapsed {
      display: none;
    }
  }

  .jv-node-row.jv-match > .jv-value,
  .jv-node-row.jv-match > .jv-key {
    outline: 1px solid var(--jv-warn);
    outline-offset: 1px;
    border-radius: 3px;
  }

  .jv-path-bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 14px;
    background: var(--jv-bg-soft);
    border-top: 1px solid var(--jv-border);
    font-size: 12px;
  }

  .jv-path-bar__row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .jv-path-label {
    color: var(--jv-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 10px;
    font-weight: 600;
    width: 42px;
    flex-shrink: 0;
  }

  .jv-path {
    flex: 1;
    min-width: 0;
    font-family: var(--jv-mono);
    font-size: 12px;
    color: var(--jv-text);
    background: transparent;
    padding: 0;
    overflow: auto;
    white-space: nowrap;
  }

  .jv-path--value {
    color: var(--jv-c-string);
  }

  .jv-path-bar button {
    padding: 3px 10px;
    font-size: 11px;
  }

  .jv-icon-btn--primary {
    background: var(--jv-accent);
    border-color: var(--jv-accent);
    color: #fff;

    &:hover {
      background: var(--jv-accent);
      border-color: var(--jv-accent);
      color: #fff;
      filter: brightness(1.08);
    }
  }

  .jv-toast {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--jv-text);
    color: var(--jv-bg);
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 13px;
    box-shadow: var(--jv-shadow);
    z-index: 999;
    animation: jv-toast-in 0.2s ease;
  }

  @keyframes jv-toast-in {
    from {
      opacity: 0;
      transform: translate(-50%, 10px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }

  .jv-tree::-webkit-scrollbar,
  textarea::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  .jv-tree::-webkit-scrollbar-track,
  textarea::-webkit-scrollbar-track {
    background: transparent;
  }
  .jv-tree::-webkit-scrollbar-thumb,
  textarea::-webkit-scrollbar-thumb {
    background: var(--jv-border);
    border-radius: 5px;

    &:hover {
      background: var(--jv-text-muted);
    }
  }

  @media (max-width: 780px) {
    .jv-layout {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 6px 1fr;
    }
    .jv-splitter {
      cursor: row-resize;
    }
    .jv-actions {
      gap: 4px;
    }
    button {
      padding: 5px 8px;
      font-size: 12px;
    }
  }
}
</style>
